import { useCallback, useMemo, useRef, useState } from "react";
import { RouteVia } from "../../../../enums/routeVia";
import {
  isCommentsRequired,
  isPaStatusReasonRequired,
} from "../utils/isCommentsRequired";

/**
 * Represents the data structure for an Astera submission form.
 * @interface AsteraSubmissionData
 * @property {string} routeVia - The route via selection
 * @property {string} paStatus - The PA status value
 * @property {string} caseStatus - The current case status
 * @property {string} comments - Additional comments for the submission
 * @property {string} key - Unique identifier for the submission
 */
interface AsteraSubmissionData {
  routeVia: string;
  paStatus: string;
  caseStatus: string;
  comments: string;
  key: string;
}

/**
 * Enum containing the keys used in AsteraSubmissionData.
 * @enum {string}
 */
export enum AsteraSubmissionDataKeys {
  ROUTE_VIA = "routeVia",
  PA_STATUS = "paStatus",
  CASE_STATUS = "caseStatus",
  COMMENTS = "comments",
  KEY = "key",
}

/**
 * Custom hook to manage Astera submission form data with cascading field resets.
 *
 * This hook provides state management for an Astera submission form where certain
 * field changes trigger resets of dependent fields:
 * - Changing `key` resets `paStatus`, `caseStatus` and `comments`
 * - Changing `routeVia` resets both `paStatus` and `caseStatus`
 * - Changing `paStatus` resets `caseStatus`
 *
 * @returns {Object} An object containing:
 * @returns {AsteraSubmissionData} asteraSubmissionData - Current form state
 * @returns {(id: string, value: string) => void} handleAsteraSubmissionDataChange - Generic handler for updating any field
 * @returns {(value: string) => void} handleKeyChange - Handler for key changes (resets paStatus, caseStatus and comments)
 * @returns {(id: string) => void} handleRouteViaChange - Handler for routeVia changes (resets paStatus and caseStatus)
 * @returns {(id: string) => void} handlePaStatusChange - Handler for paStatus changes (resets caseStatus)
 * @returns {string} initialKey - The key the row was loaded with (empty string when it had none)
 * @returns {boolean} isKeyChanged - Whether the key differs from the one the row was loaded with
 * @returns {boolean} isFormValid - Whether all required fields are filled
 *
 * @example
 * const {
 *   asteraSubmissionData,
 *   handleAsteraSubmissionDataChange,
 *   handleKeyChange,
 *   handleRouteViaChange,
 *   handlePaStatusChange,
 *   isKeyChanged,
 *   isFormValid
 * } = useAsteraSubmissionData();
 */
export type AsteraSubmissionInitialData = {
  cmmId?: string;
  routeViaValue?: string;
  responseStatusValue?: string;
  paStatusReason?: string;
  comment?: string;
};

const getInitialState = (
  initialData?: AsteraSubmissionInitialData | null,
): AsteraSubmissionData => ({
  [AsteraSubmissionDataKeys.KEY]: initialData?.cmmId ?? "",
  [AsteraSubmissionDataKeys.ROUTE_VIA]:
    initialData?.routeViaValue || RouteVia.CMM,
  [AsteraSubmissionDataKeys.PA_STATUS]: initialData?.responseStatusValue ?? "",
  [AsteraSubmissionDataKeys.CASE_STATUS]: initialData?.paStatusReason ?? "",
  [AsteraSubmissionDataKeys.COMMENTS]: initialData?.comment ?? "",
});

export const useAsteraSubmissionData = (
  initialData?: AsteraSubmissionInitialData | null,
) => {
  const initialStateRef = useRef<AsteraSubmissionData>(
    getInitialState(initialData),
  );
  const [asteraSubmissionData, setAsteraSubmissionData] =
    useState<AsteraSubmissionData>(() => initialStateRef.current);

  const handleAsteraSubmissionDataChange = useCallback(
    (id: string, value: string) => {
      setAsteraSubmissionData((prev) => ({
        ...prev,
        [id]: value,
      }));
    },
    [],
  );

  /**
   * Updates the CMM key, clearing the outcome fields when it changes.
   *
   * A different key is a different CMM case, so the PA outcome recorded against the old key no
   * longer applies and must not be re-posted (mirrors the NYCBS submission summary modal).
   * Typing the original key back restores the values the row was loaded with.
   *
   * @param value - The key entered by the user
   */
  const handleKeyChange = useCallback((value: string) => {
    const original = initialStateRef.current;
    if (value === original.key) {
      // Restore only the fields this handler clears. Replacing the whole state would also
      // revert an independent Route Via edit, which a key change never touched.
      setAsteraSubmissionData((prev) => ({
        ...prev,
        [AsteraSubmissionDataKeys.KEY]: original.key,
        [AsteraSubmissionDataKeys.PA_STATUS]: original.paStatus,
        [AsteraSubmissionDataKeys.CASE_STATUS]: original.caseStatus,
        [AsteraSubmissionDataKeys.COMMENTS]: original.comments,
      }));
      return;
    }
    setAsteraSubmissionData((prev) => ({
      ...prev,
      [AsteraSubmissionDataKeys.KEY]: value,
      [AsteraSubmissionDataKeys.PA_STATUS]: "",
      [AsteraSubmissionDataKeys.CASE_STATUS]: "",
      [AsteraSubmissionDataKeys.COMMENTS]: "",
    }));
  }, []);

  const handleRouteViaChange = useCallback((id: string) => {
    setAsteraSubmissionData((prev) => ({
      ...prev,
      [AsteraSubmissionDataKeys.ROUTE_VIA]: id,
      [AsteraSubmissionDataKeys.PA_STATUS]: "",
      [AsteraSubmissionDataKeys.CASE_STATUS]: "",
    }));
  }, []);

  const handlePaStatusChange = useCallback((id: string) => {
    setAsteraSubmissionData((prev) => ({
      ...prev,
      [AsteraSubmissionDataKeys.PA_STATUS]: id,
      [AsteraSubmissionDataKeys.CASE_STATUS]: "",
    }));
  }, []);

  const isFormValid = useMemo(() => {
    const hasRequiredFields =
      asteraSubmissionData.key !== "" &&
      asteraSubmissionData.routeVia !== "" &&
      asteraSubmissionData.paStatus !== "";

    // Comments are only required when PA Status is "Approved" or "Denied"
    const commentsValid = isCommentsRequired(asteraSubmissionData.paStatus)
      ? asteraSubmissionData.comments.trim() !== ""
      : true;

    // PA Status Reason is required when PA Status is "Denied"
    const paStatusReasonValid = isPaStatusReasonRequired(
      asteraSubmissionData.paStatus,
    )
      ? asteraSubmissionData.caseStatus.trim() !== ""
      : true;

    return hasRequiredFields && commentsValid && paStatusReasonValid;
  }, [asteraSubmissionData]);

  return {
    asteraSubmissionData,
    handleAsteraSubmissionDataChange,
    handleKeyChange,
    handleRouteViaChange,
    handlePaStatusChange,
    /** The key the row was loaded with — `""` when it had none. */
    initialKey: initialStateRef.current.key,
    isKeyChanged: asteraSubmissionData.key !== initialStateRef.current.key,
    isFormValid,
  };
};
